import { useEffect, useState, useRef } from 'react'
import { Upload, CheckCircle, Link } from 'lucide-react'
import Topbar from '../components/Topbar'
import api from '../api/client'
import useAppStore from '../store/appStore'

function fmt(v) { return v != null ? Number(v).toLocaleString('pt-PT', { minimumFractionDigits: 2 }) + '€' : '—' }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('pt-PT') : '—' }

export default function Reconciliacao() {
  const { condominioAtivo } = useAppStore()
  const [movimentos, setMovimentos] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [tab, setTab] = useState('pendentes')
  const [todos, setTodos] = useState([])
  const [modalAssociar, setModalAssociar] = useState(null)
  const [fracoes, setFracoes] = useState([])
  const [formAssoc, setFormAssoc] = useState({ fracaoId: '', categoria: 'PAGAMENTO', descricao: '' })
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')
  const [sucesso, setSucesso] = useState('')
  const fileRef = useRef()

  const loadPendentes = () => {
    if (!condominioAtivo) return
    setLoading(true)
    api.get(`/reconciliacao/${condominioAtivo.id}/pendentes`)
      .then(r => setMovimentos(r.data))
      .finally(() => setLoading(false))
  }

  const loadTodos = () => {
    if (!condominioAtivo) return
    api.get(`/reconciliacao/${condominioAtivo.id}/todos`)
      .then(r => setTodos(r.data.movimentos || []))
  }

  useEffect(() => {
    loadPendentes()
    loadTodos()
    if (condominioAtivo) {
      api.get('/fracoes', { params: { condominioId: condominioAtivo.id } })
        .then(r => setFracoes(r.data))
    }
  }, [condominioAtivo])

  const handleUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true); setSucesso(''); setErro('')
    const fd = new FormData()
    fd.append('ficheiro', file)
    try {
      const { data } = await api.post(`/reconciliacao/${condominioAtivo.id}/importar`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setSucesso(`${data.total} movimentos importados. ${data.autoAssociados} associados automaticamente.`)
      loadPendentes(); loadTodos()
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao importar ficheiro.')
    } finally { setUploading(false) }
  }

  const handleAssociar = async (e) => {
    e.preventDefault()
    setSaving(true); setErro('')
    try {
      await api.post('/reconciliacao/associar', {
        movimentoBancarioId: modalAssociar.id,
        ...formAssoc
      })
      setModalAssociar(null)
      loadPendentes(); loadTodos()
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao associar.')
    } finally { setSaving(false) }
  }

  const pendentes = movimentos.length

  return (
    <>
      <Topbar title="Reconciliação bancária" actions={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {pendentes > 0 && <span className="badge badge-amber">{pendentes} por associar</span>}
          <button className="btn btn-sm btn-primary" onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? <span className="spinner" /> : <><Upload size={13} /> Importar CSV/Excel</>}
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleUpload} />
        </div>
      } />
      <div className="page-content">
        <div className="page-header">
          <h1>Reconciliação bancária</h1>
          <p>Associação de movimentos bancários a frações e despesas</p>
        </div>

        {sucesso && <div className="alert alert-success">{sucesso}</div>}
        {erro && <div className="alert alert-error">{erro}</div>}

        <div className="tabs">
          <button className={`tab ${tab === 'pendentes' ? 'active' : ''}`} onClick={() => setTab('pendentes')}>
            Por associar {pendentes > 0 && `(${pendentes})`}
          </button>
          <button className={`tab ${tab === 'todos' ? 'active' : ''}`} onClick={() => { setTab('todos'); loadTodos() }}>
            Todos os movimentos
          </button>
        </div>

        <div className="card">
          {loading ? <div className="loading-center"><span className="spinner" /></div> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Data</th><th>Descrição</th><th>Débito</th><th>Crédito</th><th>Referência</th><th>Estado</th><th></th></tr>
                </thead>
                <tbody>
                  {(tab === 'pendentes' ? movimentos : todos).map(m => (
                    <tr key={m.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(m.data)}</td>
                      <td style={{ fontSize: 12 }}>
                        <div>{m.descricao}</div>
                        {m.sugestao && (
                          <div style={{ fontSize: 11, color: 'var(--green-dark)', marginTop: 2 }}>
                            💡 Sugestão: fração {m.sugestao.fracaoDesignacao} ({m.sugestao.confianca})
                          </div>
                        )}
                      </td>
                      <td style={{ color: 'var(--red-dark)', fontWeight: m.debito ? 600 : 400 }}>{fmt(m.debito)}</td>
                      <td style={{ color: 'var(--green-dark)', fontWeight: m.credito ? 600 : 400 }}>{fmt(m.credito)}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.referencia || '—'}</td>
                      <td>
                        <span className={`badge badge-${m.estado === 'ASSOCIADO' ? 'green' : m.estado === 'NAO_IDENTIFICADO' ? 'red' : 'amber'}`}>
                          {m.estado === 'ASSOCIADO' ? 'Associado' : m.estado === 'NAO_IDENTIFICADO' ? 'Não ID' : 'Pendente'}
                        </span>
                      </td>
                      <td>
                        {m.estado !== 'ASSOCIADO' && (
                          <button className="btn btn-sm" onClick={() => {
                            setFormAssoc({ fracaoId: m.sugestao?.fracaoDesignacao ? '' : '', categoria: 'PAGAMENTO', descricao: m.descricao })
                            setErro('')
                            setModalAssociar(m)
                          }}>
                            <Link size={12} /> Associar
                          </button>
                        )}
                        {m.estado === 'ASSOCIADO' && <CheckCircle size={16} color="var(--green)" />}
                      </td>
                    </tr>
                  ))}
                  {(tab === 'pendentes' ? movimentos : todos).length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32 }}>
                      {tab === 'pendentes' ? 'Sem movimentos por associar.' : 'Sem movimentos importados.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal associar */}
      {modalAssociar && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModalAssociar(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Associar movimento</span>
              <button onClick={() => setModalAssociar(null)} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-secondary)' }}>×</button>
            </div>
            <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 'var(--radius)', marginBottom: 16, fontSize: 13 }}>
              <strong>{modalAssociar.descricao}</strong><br />
              <span style={{ color: 'var(--text-secondary)' }}>{fmtDate(modalAssociar.data)} · {modalAssociar.credito ? `+${fmt(modalAssociar.credito)}` : `-${fmt(modalAssociar.debito)}`}</span>
            </div>
            {erro && <div className="alert alert-error">{erro}</div>}
            <form onSubmit={handleAssociar}>
              <div className="form-group">
                <label className="form-label">Fração *</label>
                <select className="form-select" value={formAssoc.fracaoId} onChange={e => setFormAssoc({...formAssoc, fracaoId: e.target.value})} required>
                  <option value="">Selecionar fração...</option>
                  {fracoes.map(f => <option key={f.id} value={f.id}>{f.designacao}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Categoria</label>
                <select className="form-select" value={formAssoc.categoria} onChange={e => setFormAssoc({...formAssoc, categoria: e.target.value})}>
                  <option value="PAGAMENTO">Pagamento de quota</option>
                  <option value="FUNDO_RESERVA">Fundo de reserva</option>
                  <option value="OUTRO">Outro</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Descrição</label>
                <input className="form-input" value={formAssoc.descricao} onChange={e => setFormAssoc({...formAssoc, descricao: e.target.value})} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModalAssociar(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <span className="spinner" /> : 'Associar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
